// LeaderboardDetailPage — sticky shell + 5-tab nav for a single leaderboard.
//
// Ports the "Overview" tab from docs/mockups/leaderboard-detail.html (L600–793).
// Tabs other than `overview` render a placeholder until T12–T15 land.
//
// URL contract: `?tab=overview|cycles|rewards|audit|settings`. The current tab
// lives in the query string so deep links + browser back/forward work and the
// PageHeader's breadcrumb stays stable across tab switches.

import { useMemo } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MoreVertical } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { ApiError, describeError } from "../../lib/errors";
import { formatRelativeShort } from "../../lib/format";
import { getProject } from "../projects/api";
import {
  getLeaderboard,
  lbKeys,
  listAudit,
  listCycleTop,
  listCycles,
} from "./api";
import type {
  AuditEntry,
  Cycle,
  Entry,
  Leaderboard,
} from "./types";
import { AuditTab } from "./tabs/AuditTab";
import { CyclesTab } from "./tabs/CyclesTab";
import { RewardsTab } from "./tabs/RewardsTab";
import { SettingsTab } from "./tabs/SettingsTab";
import styles from "./LeaderboardDetailPage.module.css";

// ──────────────────────────────────────────────────────────────────────────────
// Badge helpers — local copies of LeaderboardsListPage.tsx::windowBadgeFor /
// operatorBadgeFor / scopeLabelFor. The list page does not export them and is
// locked by this task's brief, so we duplicate verbatim here.
// TODO(T11+): unify with LeaderboardsListPage badge helpers in lib/leaderboardBadges.tsx.
// ──────────────────────────────────────────────────────────────────────────────

function describeCron(cron: string | undefined): string {
  if (!cron) return "recurring";
  const trimmed = cron.trim();
  const parts = trimmed.split(/\s+/);
  if (parts.length === 5) {
    const [, , dom, mon, dow] = parts;
    if (dom === "*" && mon === "*" && /^[A-Z0-6]+$/i.test(dow) && dow !== "*") {
      const map: Record<string, string> = {
        "0": "SUN", "7": "SUN",
        "1": "MON", "2": "TUE", "3": "WED", "4": "THU", "5": "FRI", "6": "SAT",
        SUN: "SUN", MON: "MON", TUE: "TUE", WED: "WED", THU: "THU", FRI: "FRI", SAT: "SAT",
      };
      const day = map[dow.toUpperCase()] ?? dow.toUpperCase();
      return `weekly @ ${day}`;
    }
    if (dow === "*" && mon === "*" && /^\d+$/.test(dom)) {
      return `monthly @ ${dom}`;
    }
    if (dow === "*" && dom === "*" && mon === "*") {
      return "daily";
    }
  }
  return trimmed;
}

interface WindowBadgeSpec {
  variant: "info" | "warn" | "neutral";
  text: string;
}

function windowBadgeFor(lb: Leaderboard): WindowBadgeSpec {
  if (lb.window_kind === "recurring") {
    return { variant: "info", text: describeCron(lb.cron) };
  }
  if (lb.window_kind === "event") {
    return { variant: "warn", text: "event" };
  }
  return { variant: "neutral", text: "all-time" };
}

function operatorBadgeFor(lb: Leaderboard): { glyph: string; label: string } {
  switch (lb.operator) {
    case "best":
    case "worst":
      return { glyph: lb.sort === "asc" ? "↑" : "↓", label: lb.operator };
    case "increment":
      return { glyph: "Σ", label: "increment" };
    case "set":
      return { glyph: "=", label: "set" };
    default:
      return { glyph: "·", label: lb.operator };
  }
}

function scopeLabelFor(lb: Leaderboard): string {
  if (lb.scope === "guild_aggregated") {
    return `guild_aggregated (${lb.guild_aggregator ?? "sum"})`;
  }
  return lb.scope;
}

// ──────────────────────────────────────────────────────────────────────────────
// Avatar palette — matches the mockup's gradient-pair scheme for the top-5 table
// ──────────────────────────────────────────────────────────────────────────────

const AVATAR_PALETTE: Array<[string, string]> = [
  ["#14b8a6", "#0d9488"], // teal
  ["#3b82f6", "#1d4ed8"], // blue
  ["#f59e0b", "#d97706"], // amber
  ["#8b5cf6", "#6366f1"], // violet
  ["#ef4444", "#b91c1c"], // red
];

function avatarColors(seed: string): [string, string] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

// ──────────────────────────────────────────────────────────────────────────────
// Time / number formatting
// ──────────────────────────────────────────────────────────────────────────────

const ABS_FMT = new Intl.DateTimeFormat("en-US", {
  year: "numeric", month: "short", day: "2-digit",
  hour: "2-digit", minute: "2-digit", hour12: false,
});

/** Short weekday for the progress strip (e.g. "Mon" / "Sun"). */
const WEEKDAY_FMT = new Intl.DateTimeFormat("en-US", { weekday: "short" });

function fmtAbs(iso: string | undefined | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return ABS_FMT.format(d);
}

function fmtNumber(n: number | undefined | null): string {
  if (n === undefined || n === null || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US");
}

/** Compact humanised diff like "3d 4h", "2h 15m", "5m". Negative → "closed". */
function humaniseUntil(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const secs = Math.floor((d.getTime() - Date.now()) / 1000);
  if (secs <= 0) return "closed";
  const days = Math.floor(secs / 86400);
  const hrs = Math.floor((secs % 86400) / 3600);
  const mins = Math.floor((secs % 3600) / 60);
  if (days > 0) return `${days}d ${hrs}h`;
  if (hrs > 0) return `${hrs}h ${mins}m`;
  return `${mins}m`;
}

/** 0..1 progress through [open_at, close_at]; clamped. NaN→0. */
function cycleProgress(c: Cycle | undefined): number {
  if (!c?.close_at) return 0;
  const start = new Date(c.open_at).getTime();
  const end = new Date(c.close_at).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  const now = Date.now();
  if (now <= start) return 0;
  if (now >= end) return 1;
  return (now - start) / (end - start);
}

function shortId(id: string | undefined, n = 8): string {
  if (!id) return "—";
  return id.replace(/-/g, "").slice(0, n);
}

// ──────────────────────────────────────────────────────────────────────────────
// Tab definitions
// ──────────────────────────────────────────────────────────────────────────────

type TabId = "overview" | "cycles" | "rewards" | "audit" | "settings";
const TAB_ORDER: TabId[] = ["overview", "cycles", "rewards", "audit", "settings"];
const TAB_LABEL: Record<TabId, string> = {
  overview: "Overview",
  cycles: "Cycles",
  rewards: "Rewards",
  audit: "Audit",
  settings: "Settings",
};

function isTabId(v: string | null): v is TabId {
  return v === "overview" || v === "cycles" || v === "rewards" || v === "audit" || v === "settings";
}

// ──────────────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────────────

export function LeaderboardDetailPage() {
  const { id: gameId = "", lbId = "" } = useParams<{ id: string; lbId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const rawTab = searchParams.get("tab");
  const tab: TabId = isTabId(rawTab) ? rawTab : "overview";

  // Project name for the breadcrumb (same pattern as the list page).
  const projectQ = useQuery({
    queryKey: ["project", gameId],
    queryFn: () => getProject(gameId).then((r) => r.data),
    enabled: !!gameId,
  });

  const lbQ = useQuery({
    queryKey: lbKeys.detail(gameId, lbId),
    queryFn: () => getLeaderboard(gameId, lbId),
    enabled: !!gameId && !!lbId,
  });

  // === Not-found short-circuit ======================================== //
  if (lbQ.isError && lbQ.error instanceof ApiError && lbQ.error.code === "not_found") {
    return (
      <>
        <PageHeader
          breadcrumb={[
            { label: "All projects", to: "/projects" },
            {
              label: projectQ.data?.name ?? "Project",
              to: `/projects/${gameId}`,
            },
            { label: "Leaderboards", to: `/projects/${gameId}/leaderboards` },
          ]}
          title="Not found"
        />
        <div className={styles.page}>
          <Alert variant="danger" title="Leaderboard not found">
            We couldn't find the leaderboard you were looking for. It may have
            been archived or the link may be incorrect.
          </Alert>
          <div className={styles.errorActions}>
            <Link to={`/projects/${gameId}/leaderboards`} className={styles.backLink}>
              ← Back to leaderboards
            </Link>
          </div>
        </div>
      </>
    );
  }

  const lb = lbQ.data;
  const lbName = lb?.name ?? "Leaderboard";

  const header = (
    <PageHeader
      breadcrumb={[
        { label: "All projects", to: "/projects" },
        {
          label: projectQ.data?.name ?? "Project",
          to: `/projects/${gameId}`,
        },
        { label: "Leaderboards", to: `/projects/${gameId}/leaderboards` },
      ]}
      title={lbName}
    />
  );

  // === Other error states ============================================= //
  if (lbQ.isError) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <Alert variant="danger" title="Couldn't load leaderboard">
            {lbQ.error instanceof ApiError
              ? describeError(lbQ.error.code)
              : describeError("internal")}
          </Alert>
        </div>
      </>
    );
  }

  // === Loading state (skeleton header) ================================ //
  if (!lb) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.detailHead}>
            <Skeleton width={220} height={24} />
            <div style={{ marginTop: 10 }}>
              <Skeleton width={420} height={12} />
            </div>
          </div>
          <div className={styles.tabBar}>
            {TAB_ORDER.map((id) => (
              <span key={id} className={styles.tab}>
                {TAB_LABEL[id]}
              </span>
            ))}
          </div>
          <div className={styles.tabBody}>
            <Skeleton height={180} />
          </div>
        </div>
      </>
    );
  }

  // === Sticky detail head ============================================= //
  return (
    <>
      {header}
      <div className={styles.page}>
        <DetailHead lb={lb} />
        <TabBar active={tab} />
        <div className={styles.tabBody}>
          {tab === "overview" && (
            <OverviewTab gameId={gameId} lbId={lbId} lb={lb} />
          )}
          {tab === "cycles" && <CyclesTab gameId={gameId} lbId={lbId} />}
          {tab === "rewards" && <RewardsTab gameId={gameId} lbId={lbId} />}
          {tab === "audit" && <AuditTab gameId={gameId} lbId={lbId} />}
          {tab === "settings" && (
            <SettingsTab
              gameId={gameId}
              lbId={lbId}
              lb={lb}
              onArchived={() =>
                navigate(`/projects/${gameId}/leaderboards`)
              }
            />
          )}
        </div>
      </div>
    </>
  );
}

// === Sticky detail head ============================================== //

function DetailHead({ lb }: { lb: Leaderboard }) {
  const wb = windowBadgeFor(lb);
  return (
    <div className={styles.detailHead}>
      <div className={styles.row1}>
        <div className={styles.lbTitle}>{lb.name}</div>
        <StatusPill status={lb.status} />
        <div className={styles.spacer} />
        <button
          type="button"
          className={styles.kebab}
          aria-label="Leaderboard actions"
          title="Actions (coming soon)"
          disabled
        >
          <MoreVertical size={16} aria-hidden="true" />
        </button>
      </div>
      {lb.description && (
        <div className={styles.secondaryMuted}>{lb.description}</div>
      )}
      <div className={styles.secondary}>
        <code className={styles.miniMono}>{lb.ext_key}</code>
        <span className={styles.sep}>·</span>
        <span className={styles.segVal}>{lb.operator}</span>
        <span className={styles.sep}>·</span>
        <span className={styles.segVal}>{lb.sort}</span>
        <span className={styles.sep}>·</span>
        <span className={styles.segVal}>{wb.text}</span>
        <span className={styles.sep}>·</span>
        <span className={styles.segVal}>{lb.visibility}</span>
        <span className={styles.sep}>·</span>
        <span className={styles.segVal}>
          {lb.segmentation === "tier" && lb.tier_mode
            ? `tier (${lb.tier_mode})`
            : lb.segmentation}
        </span>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: Leaderboard["status"] }) {
  const cls =
    status === "active"
      ? styles.statusActive
      : status === "paused"
        ? styles.statusPaused
        : styles.statusArchived;
  return (
    <span className={`${styles.statusPill} ${cls}`}>
      <span className={styles.statusDot} aria-hidden="true" />
      {status}
    </span>
  );
}

// === Tab bar ========================================================= //

function TabBar({ active }: { active: TabId }) {
  return (
    <div role="tablist" className={styles.tabBar}>
      {TAB_ORDER.map((id) => {
        const isActive = id === active;
        return (
          <Link
            key={id}
            role="tab"
            aria-selected={isActive}
            to={`?tab=${id}`}
            className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
          >
            {TAB_LABEL[id]}
          </Link>
        );
      })}
    </div>
  );
}

// === Overview tab ==================================================== //

function OverviewTab({
  gameId,
  lbId,
  lb,
}: {
  gameId: string;
  lbId: string;
  lb: Leaderboard;
}) {
  const cyclesQ = useQuery({
    queryKey: lbKeys.cycles(gameId, lbId),
    queryFn: () => listCycles(gameId, lbId),
    enabled: !!gameId && !!lbId,
  });

  const currentCycle: Cycle | undefined = useMemo(() => {
    const cs = cyclesQ.data?.cycles ?? [];
    return cs.find((c) => c.status === "open") ?? cs[0];
  }, [cyclesQ.data]);

  const topQ = useQuery({
    queryKey: lbKeys.top(gameId, lbId, currentCycle?.id ?? "", { limit: 5 }),
    queryFn: () => {
      if (!currentCycle) return Promise.reject(new Error("no current cycle"));
      return listCycleTop(gameId, lbId, currentCycle.id, { limit: 5 });
    },
    enabled: !!currentCycle,
  });

  const auditQ = useQuery({
    queryKey: lbKeys.audit(gameId, lbId, currentCycle?.id ?? "", { limit: 5 }),
    queryFn: () => {
      if (!currentCycle) return Promise.reject(new Error("no current cycle"));
      return listAudit(gameId, lbId, currentCycle.id, { limit: 5 });
    },
    enabled: !!currentCycle,
  });

  const entries: Entry[] = topQ.data?.entries ?? [];
  const auditRows: AuditEntry[] = auditQ.data?.audit ?? [];

  return (
    <div className={styles.overviewGrid}>
      <HeroCard
        cycle={currentCycle}
        topEntry={entries[0]}
        loading={cyclesQ.isLoading}
        error={cyclesQ.isError ? cyclesQ.error : null}
      />
      <TopFiveCard
        cycle={currentCycle}
        entries={entries}
        loading={topQ.isLoading}
        error={topQ.isError ? topQ.error : null}
      />
      <ConfigSummaryCard lb={lb} />
      <RecentAuditCard
        rows={auditRows}
        loading={auditQ.isLoading}
        error={auditQ.isError ? auditQ.error : null}
      />
    </div>
  );
}

/** Render a short inline error blurb inside a card body. Centralised so the
 * three Overview sub-cards (Hero/Top-5/Recent audit) format errors identically.
 */
function describeQueryError(error: unknown): string {
  if (error instanceof ApiError) return describeError(error.code);
  return describeError("internal");
}

// === Card 1 — Hero =================================================== //

function HeroCard({
  cycle,
  topEntry,
  loading,
  error,
}: {
  cycle: Cycle | undefined;
  topEntry: Entry | undefined;
  loading: boolean;
  error: unknown;
}) {
  if (error) {
    return (
      <section className={`${styles.card} ${styles.span2}`}>
        <CardHeader title="Current cycle" sub="live · refreshes every 30s" />
        <div className={styles.cardBody}>
          <div className={styles.cardError}>
            Couldn&apos;t load cycles — {describeQueryError(error)}
          </div>
        </div>
      </section>
    );
  }
  if (loading) {
    return (
      <section className={`${styles.card} ${styles.span2}`}>
        <CardHeader title="Current cycle" sub="live · refreshes every 30s" />
        <div className={styles.cardBody}>
          <Skeleton height={120} />
        </div>
      </section>
    );
  }
  if (!cycle) {
    return (
      <section className={`${styles.card} ${styles.span2}`}>
        <CardHeader title="Current cycle" sub="live · refreshes every 30s" />
        <div className={styles.cardBody}>
          <div className={styles.placeholder}>No active cycle.</div>
        </div>
      </section>
    );
  }
  const progress = cycleProgress(cycle);
  const openLabel = WEEKDAY_FMT.format(new Date(cycle.open_at));
  const closeLabel = cycle.close_at
    ? WEEKDAY_FMT.format(new Date(cycle.close_at))
    : "—";
  return (
    <section className={`${styles.card} ${styles.span2}`}>
      <CardHeader title="Current cycle" sub="live · refreshes every 30s" />
      <div className={styles.cardBody}>
        <div className={styles.hero}>
          <div className={styles.heroRow}>
            <span className={styles.heroCid}>{shortId(cycle.id)}</span>
            <CycleStatusPill status={cycle.status} />
            <span className={styles.heroOpened}>
              Opened {fmtAbs(cycle.open_at)} · Closes {fmtAbs(cycle.close_at)}
            </span>
          </div>
          <div className={styles.heroStats}>
            <div className={styles.heroStat}>
              <div className={styles.heroLbl}>Time until close</div>
              <div className={styles.heroVal} title={fmtAbs(cycle.close_at)}>
                {humaniseUntil(cycle.close_at)}
              </div>
              <div className={styles.heroSub}>≈ {fmtAbs(cycle.close_at)}</div>
            </div>
            <div className={styles.heroStat}>
              <div className={styles.heroLbl}>Participants</div>
              <div className={styles.heroVal}>{fmtNumber(cycle.participants)}</div>
              <div className={styles.heroSub}>across this cycle</div>
            </div>
            <div className={styles.heroStat}>
              <div className={styles.heroLbl}>Top score</div>
              <div className={styles.heroVal}>
                {topEntry ? fmtNumber(topEntry.score) : "—"}
              </div>
              <div className={styles.heroSub}>
                {topEntry
                  ? `${topEntry.player.display_name} · ${shortId(topEntry.player.id)}`
                  : "—"}
              </div>
            </div>
          </div>
          {cycle.close_at && (
            <div className={styles.progressRow}>
              <span>{openLabel}</span>
              <div className={styles.progress}>
                <div
                  className={styles.progressBar}
                  style={{ width: `${(progress * 100).toFixed(1)}%` }}
                />
              </div>
              <span>{closeLabel}</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function CycleStatusPill({ status }: { status: Cycle["status"] }) {
  const cls =
    status === "open"
      ? styles.cycleOpen
      : status === "closing"
        ? styles.cycleClosing
        : status === "scheduled"
          ? styles.cycleScheduled
          : styles.cycleClosed;
  return (
    <span className={`${styles.statusPill} ${cls}`}>
      <span className={styles.statusDot} aria-hidden="true" />
      {status}
    </span>
  );
}

// === Card 2 — Top-5 ================================================== //

function TopFiveCard({
  cycle,
  entries,
  loading,
  error,
}: {
  cycle: Cycle | undefined;
  entries: Entry[];
  loading: boolean;
  error: unknown;
}) {
  return (
    <section className={styles.card}>
      <CardHeader title="Top 5" sub={cycle ? shortId(cycle.id) : ""} />
      {error ? (
        <div className={styles.cardBody}>
          <div className={styles.cardError}>
            Couldn&apos;t load top — {describeQueryError(error)}
          </div>
        </div>
      ) : loading ? (
        <div className={styles.cardBody}>
          <Skeleton height={140} />
        </div>
      ) : entries.length === 0 ? (
        <div className={styles.cardBody}>
          <div className={styles.placeholder}>No entries yet.</div>
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ width: 48 }}>#</th>
              <th>Entity</th>
              <th className={styles.num}>Score</th>
            </tr>
          </thead>
          <tbody>
            {entries.slice(0, 5).map((e) => {
              const rankCls =
                e.rank === 1
                  ? styles.rankGold
                  : e.rank === 2
                    ? styles.rankSilver
                    : e.rank === 3
                      ? styles.rankBronze
                      : "";
              const [a, b] = avatarColors(e.player.id);
              return (
                <tr key={e.player.id}>
                  <td className={`${styles.rank} ${rankCls}`}>{e.rank}</td>
                  <td>
                    <div className={styles.entityCell}>
                      <span
                        className={styles.avSm}
                        style={{ background: `linear-gradient(135deg, ${a}, ${b})` }}
                        aria-hidden="true"
                      >
                        {initials(e.player.display_name)}
                      </span>
                      <span className={styles.entityName}>
                        {e.player.display_name}
                      </span>
                    </div>
                  </td>
                  <td className={`${styles.num} ${styles.score}`}>
                    {fmtNumber(e.score)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <div className={styles.cardFooter}>
        <Link to="?tab=cycles">View full top →</Link>
      </div>
    </section>
  );
}

// === Card 3 — Config summary ========================================= //

function ConfigSummaryCard({ lb }: { lb: Leaderboard }) {
  const wb = windowBadgeFor(lb);
  const op = operatorBadgeFor(lb);
  return (
    <section className={styles.card}>
      <CardHeader
        title="Config summary"
        sub={
          <>
            read-only ·{" "}
            <Link to="?tab=settings" className={styles.cardSubLink}>
              Edit →
            </Link>
          </>
        }
      />
      <div className={styles.cardBody}>
        <div className={styles.summaryGrid}>
          <SummaryRow label="Operator">
            <span className={`${styles.badge} ${styles.badgeNeutral}`}>
              <span className={styles.glyph}>{op.glyph}</span> {op.label}
            </span>
          </SummaryRow>
          <SummaryRow label="Sort">
            <span className={`${styles.badge} ${styles.badgeNeutral} ${styles.badgeMono}`}>
              {lb.sort}
            </span>
          </SummaryRow>
          <SummaryRow label="Window">
            <span
              className={`${styles.badge} ${
                wb.variant === "info"
                  ? styles.badgeInfo
                  : wb.variant === "warn"
                    ? styles.badgeWarn
                    : styles.badgeNeutral
              }`}
            >
              {lb.window_kind}
            </span>
            {/* TODO(window-tz): show timezone next to cron once Leaderboard
                DTO exposes a timezone column (Go-truth currently has none). */}
            {lb.cron && <code className={styles.miniCode}>{lb.cron}</code>}
          </SummaryRow>
          <SummaryRow label="Scope">
            <span className={`${styles.badge} ${styles.badgeNeutral}`}>
              {scopeLabelFor(lb)}
            </span>
          </SummaryRow>
          <SummaryRow label="Segmentation">
            <span className={`${styles.badge} ${styles.badgeNeutral}`}>
              {lb.segmentation}
            </span>
            {lb.segmentation === "tier" && lb.tier_mode && (
              <span className={styles.summaryMuted}>
                tier_mode: <code className={styles.miniCode}>{lb.tier_mode}</code>
              </span>
            )}
          </SummaryRow>
          <SummaryRow label="Visibility">
            <span className={`${styles.badge} ${styles.badgeNeutral}`}>
              {lb.visibility}
            </span>
          </SummaryRow>
          <SummaryRow label="Submit policy">
            <span className={`${styles.badge} ${styles.badgeNeutral} ${styles.badgeMono}`}>
              {lb.submit_policy}
            </span>
          </SummaryRow>
          <SummaryRow label="Realtime">
            {lb.realtime ? (
              <span className={`${styles.badge} ${styles.badgeOk}`}>on</span>
            ) : (
              <span className={`${styles.badge} ${styles.badgeNeutral}`}>off</span>
            )}
            {lb.realtime && (
              <span className={styles.summaryMuted}>rank.changed WS</span>
            )}
          </SummaryRow>
        </div>
      </div>
    </section>
  );
}

function SummaryRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.summaryRow}>
      <div className={styles.summaryKey}>{label}</div>
      <div className={styles.summaryValue}>{children}</div>
    </div>
  );
}

// === Card 4 — Recent audit =========================================== //

function RecentAuditCard({
  rows,
  loading,
  error,
}: {
  rows: AuditEntry[];
  loading: boolean;
  error: unknown;
}) {
  return (
    <section className={`${styles.card} ${styles.span2}`}>
      <CardHeader title="Recent audit" sub="last 5 events" />
      {error ? (
        <div className={styles.cardBody}>
          <div className={styles.cardError}>
            Couldn&apos;t load audit — {describeQueryError(error)}
          </div>
        </div>
      ) : loading ? (
        <div className={styles.cardBody}>
          <Skeleton height={120} />
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.cardBody}>
          <div className={styles.placeholder}>No audit events yet.</div>
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ width: 100 }}>When</th>
              <th style={{ width: 70 }}>Channel</th>
              <th style={{ width: 200 }}>Actor</th>
              <th style={{ width: 140 }}>Entity</th>
              <th>Score</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 5).map((row) => {
              const rejected =
                row.applied_score === undefined ||
                row.applied_score === null ||
                !!row.reject_reason;
              // TODO(audit-enrich): expose actor identity per channel —
              // client/s2s currently have no actor field in AuditEntry DTO.
              const actorCell =
                row.channel === "admin" && row.admin_user_id ? (
                  <span className={styles.monoCell}>
                    {shortId(row.admin_user_id)}
                  </span>
                ) : (
                  <span className={styles.muted}>—</span>
                );
              return (
                <tr key={row.id} className={styles.auditRow}>
                  <td
                    className={styles.muted}
                    title={fmtAbs(row.created_at)}
                  >
                    {formatRelativeShort(row.created_at)}
                  </td>
                  <td>
                    <ChannelBadge channel={row.channel} />
                  </td>
                  <td>{actorCell}</td>
                  {/* TODO(audit-enrich): expand AuditEntry with
                      entity_display_name once Go backend supports
                      ?expand=entity for /audit. */}
                  <td className={styles.monoCell}>{shortId(row.entity_id)}</td>
                  <td>
                    <span
                      className={`${styles.scoreFlow} ${
                        rejected ? styles.scoreRejected : ""
                      }`}
                      title={row.reject_reason}
                    >
                      {fmtNumber(row.submitted_score)}
                      <span className={styles.scoreArrow}>→</span>
                      {rejected ? (
                        <span className={styles.scoreX}>✗</span>
                      ) : (
                        fmtNumber(row.applied_score ?? null)
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <div className={styles.cardFooter}>
        <Link to="?tab=audit">View all →</Link>
      </div>
    </section>
  );
}

function ChannelBadge({ channel }: { channel: AuditEntry["channel"] }) {
  const cls =
    channel === "client"
      ? styles.chClient
      : channel === "s2s"
        ? styles.chS2s
        : styles.chAdmin;
  return <span className={`${styles.chBadge} ${cls}`}>{channel}</span>;
}

// === Card chrome ===================================================== //

function CardHeader({
  title,
  sub,
}: {
  title: string;
  sub?: React.ReactNode;
}) {
  return (
    <div className={styles.cardHeader}>
      <h3 className={styles.cardTitle}>{title}</h3>
      {sub && <span className={styles.cardSub}>{sub}</span>}
    </div>
  );
}
